import { useState, useEffect } from 'react';
import { useTranslation, TFunction } from 'react-i18next';
import { useTheme } from '../../../hooks/useTheme';

const WHATSAPP_DR = 'https://wa.me/18495388899?text=Hola%20InnoFlex!%20Tengo%20una%20pregunta.';

interface FAQItem {
  question: string;
  answer: string;
  icon: string;
}

interface Category {
  id: string;
  label: string;
  icon: string;
  color: string;
  isNew?: boolean;
  faqs: FAQItem[];
}

function getCategories(t: TFunction): Category[] {
  return [
    {
      id: 'sistema',
      label: t('faq_cat_sistema'),
      icon: 'ri-cpu-line',
      color: '#00cfff',
      faqs: [
        { icon: 'ri-layout-grid-line', question: t('faq_s1_q'), answer: t('faq_s1_a') },
        { icon: 'ri-wifi-off-line',    question: t('faq_s2_q'), answer: t('faq_s2_a') },
        { icon: 'ri-device-line',      question: t('faq_s3_q'), answer: t('faq_s3_a') },
      ],
    },
    {
      id: 'implementacion',
      label: t('faq_cat_implementacion'),
      icon: 'ri-rocket-2-line',
      color: '#00ff9d',
      faqs: [
        { icon: 'ri-timer-flash-line', question: t('faq_i1_q'), answer: t('faq_i1_a') },
        { icon: 'ri-map-pin-2-line',   question: t('faq_i2_q'), answer: t('faq_i2_a') },
      ],
    },
    {
      id: 'facturacion',
      label: t('faq_cat_facturacion'),
      icon: 'ri-file-shield-2-line',
      color: '#facc15',
      faqs: [
        { icon: 'ri-government-line',    question: t('faq_f1_q'), answer: t('faq_f1_a') },
        { icon: 'ri-receipt-line',       question: t('faq_f2_q'), answer: t('faq_f2_a') },
        { icon: 'ri-shield-star-line',   question: t('faq_f3_q'), answer: t('faq_f3_a') },
        { icon: 'ri-global-line',        question: t('faq_f4_q'), answer: t('faq_f4_a') },
        { icon: 'ri-add-circle-line',    question: t('faq_f5_q'), answer: t('faq_f5_a') },
        { icon: 'ri-shopping-cart-line', question: t('faq_f6_q'), answer: t('faq_f6_a') },
      ],
    },
    {
      id: 'republicafel',
      label: t('faq_cat_republicafel'),
      icon: 'ri-building-4-line',
      color: '#f97316',
      isNew: true,
      faqs: [
        { icon: 'ri-information-line',        question: t('faq_rfel1_q'), answer: t('faq_rfel1_a') },
        { icon: 'ri-user-settings-line',      question: t('faq_rfel2_q'), answer: t('faq_rfel2_a') },
        { icon: 'ri-link-m',                  question: t('faq_rfel3_q'), answer: t('faq_rfel3_a') },
        { icon: 'ri-dashboard-3-line',        question: t('faq_rfel4_q'), answer: t('faq_rfel4_a') },
        { icon: 'ri-money-dollar-circle-line',question: t('faq_rfel5_q'), answer: t('faq_rfel5_a') },
        { icon: 'ri-whatsapp-line',           question: t('faq_rfel6_q'), answer: t('faq_rfel6_a') },
        { icon: 'ri-calendar-check-line',     question: t('faq_rfel7_q'), answer: t('faq_rfel7_a') },
      ],
    },
    {
      id: 'digital_dev',
      label: t('faq_cat_digital_dev'),
      icon: 'ri-smartphone-line',
      color: '#a78bfa',
      isNew: true,
      faqs: [
        { icon: 'ri-time-line',         question: t('faq_dd1_q'), answer: t('faq_dd1_a') },
        { icon: 'ri-price-tag-3-line',  question: t('faq_dd2_q'), answer: t('faq_dd2_a') },
        { icon: 'ri-code-s-slash-line', question: t('faq_dd3_q'), answer: t('faq_dd3_a') },
        { icon: 'ri-tools-line',        question: t('faq_dd4_q'), answer: t('faq_dd4_a') },
        { icon: 'ri-server-line',       question: t('faq_dd5_q'), answer: t('faq_dd5_a') },
      ],
    },
    {
      id: 'desarrollo',
      label: t('faq_cat_desarrollo'),
      icon: 'ri-code-s-slash-line',
      color: '#00cfff',
      faqs: [
        { icon: 'ri-settings-4-line',     question: t('faq_d1_q'), answer: t('faq_d1_a') },
        { icon: 'ri-expand-up-down-line', question: t('faq_d2_q'), answer: t('faq_d2_a') },
      ],
    },
    {
      id: 'planes',
      label: t('faq_cat_planes'),
      icon: 'ri-price-tag-3-line',
      color: '#00ff9d',
      faqs: [
        { icon: 'ri-refresh-line', question: t('faq_p1_q'), answer: t('faq_p1_a') },
        { icon: 'ri-eye-line',     question: t('faq_p2_q'), answer: t('faq_p2_a') },
      ],
    },
  ];
}

interface QuestionCardProps {
  faq: FAQItem;
  color: string;
  isOpen: boolean;
  onToggle: () => void;
  index: number;
  isLight: boolean;
}

function QuestionCard({ faq, color, isOpen, onToggle, index, isLight }: QuestionCardProps) {
  return (
    <div
      className="rounded-2xl border transition-all duration-300 overflow-hidden cursor-pointer"
      style={{
        borderColor: isOpen
          ? color + '50'
          : isLight ? 'rgba(10,22,40,0.10)' : 'rgba(255,255,255,0.07)',
        background: isOpen
          ? isLight
            ? `linear-gradient(135deg, ${color}12 0%, rgba(248,251,255,1) 70%)`
            : `linear-gradient(135deg, ${color}0a 0%, rgba(10,20,40,0.97) 70%)`
          : isLight ? '#ffffff' : 'rgba(10,20,40,0.6)',
        animationDelay: `${index * 60}ms`,
        boxShadow: isLight && isOpen ? `0 2px 12px ${color}15` : 'none',
      }}
      onClick={onToggle}
    >
      <div className="flex items-start gap-4 p-5">
        {/* Icon */}
        <div
          className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 transition-all duration-300"
          style={{
            backgroundColor: isOpen
              ? color + '22'
              : isLight ? 'rgba(10,22,40,0.06)' : 'rgba(255,255,255,0.05)',
            color: isOpen
              ? color
              : isLight ? 'rgba(10,22,40,0.35)' : 'rgba(255,255,255,0.35)',
          }}
        >
          <i className={`${faq.icon} text-base`}></i>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <h4
              className="font-heading text-sm font-bold leading-snug transition-colors duration-200"
              style={{
                color: isOpen
                  ? isLight ? '#0a1628' : '#fff'
                  : isLight ? 'rgba(10,22,40,0.82)' : 'rgba(255,255,255,0.80)',
              }}
            >
              {faq.question}
            </h4>
            <div
              className="w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300"
              style={{
                backgroundColor: isOpen
                  ? color + '22'
                  : isLight ? 'rgba(10,22,40,0.06)' : 'rgba(255,255,255,0.06)',
                color: isOpen
                  ? color
                  : isLight ? 'rgba(10,22,40,0.35)' : 'rgba(255,255,255,0.35)',
                transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
              }}
            >
              <i className="ri-add-line text-xs font-bold"></i>
            </div>
          </div>

          {/* Answer */}
          <div
            className="overflow-hidden transition-all duration-350"
            style={{
              maxHeight: isOpen ? '200px' : '0px',
              opacity: isOpen ? 1 : 0,
              marginTop: isOpen ? '12px' : '0px',
              transition: 'max-height 0.35s ease, opacity 0.25s ease, margin-top 0.25s ease',
            }}
          >
            <div
              className="w-full h-px mb-3"
              style={{ backgroundColor: color + '20' }}
            />
            <p
              className="font-body text-xs leading-relaxed"
              style={{ color: isLight ? 'rgba(10,22,40,0.62)' : 'rgba(255,255,255,0.60)' }}
            >
              {faq.answer}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FAQ() {
  const [activeCategory, setActiveCategory] = useState('sistema');
  const [openQuestion, setOpenQuestion] = useState<string | null>(null);
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isLight = theme === 'light';

  const categories = getCategories(t);
  const current = categories.find((c) => c.id === activeCategory)!;

  // Listen for external category selection (e.g. from Navbar dropdown)
  useEffect(() => {
    const handler = (e: Event) => {
      const cat = (e as CustomEvent<{ cat: string }>).detail?.cat;
      if (cat) {
        setActiveCategory(cat);
        setOpenQuestion(null);
      }
    };
    window.addEventListener('innoflex:select-faq', handler);
    return () => window.removeEventListener('innoflex:select-faq', handler);
  }, []);

  const handleCategoryChange = (id: string) => {
    setActiveCategory(id);
    setOpenQuestion(null);
  };

  const toggleQuestion = (q: string) => {
    setOpenQuestion(openQuestion === q ? null : q);
  };

  return (
    <section id="faq" className="bg-navy-950 py-12 md:py-24 px-4 sm:px-5 md:px-6 relative overflow-hidden">

      {/* Decorative bg glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(ellipse, ${current.color}06 0%, transparent 70%)`,
          filter: 'blur(60px)',
          transition: 'background 0.5s ease',
        }}
      />

      <div className="max-w-5xl mx-auto relative z-10">

        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <div
            className="font-heading font-black leading-none select-none pointer-events-none mb-1"
            style={{
              fontSize: 'clamp(48px, 12vw, 148px)',
              background: 'linear-gradient(90deg, #00cfff22, #00ff9d22)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '0.08em',
            }}
          >
            FAQ
          </div>

          <span className="inline-block text-electric text-xs md:text-sm font-semibold tracking-widest uppercase font-body mb-2 md:mb-3">
            {t('faq_label')}
          </span>
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2 md:mb-3 leading-tight">
            {t('faq_title_1')}{' '}
            <span style={{ background: 'linear-gradient(90deg, #00cfff, #00ff9d)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {t('faq_title_2')}
            </span>
          </h2>
          <p
            className="font-body text-xs md:text-sm max-w-lg mx-auto"
            style={{ color: isLight ? 'rgba(10,22,40,0.50)' : 'rgba(255,255,255,0.50)' }}
          >
            {t('faq_cat_sub')}
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-7 md:mb-10">
          {categories.map((cat) => {
            const isActive = cat.id === activeCategory;
            return (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                className="relative flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold font-body transition-all duration-250 cursor-pointer whitespace-nowrap"
                style={{
                  backgroundColor: isActive
                    ? cat.color + '18'
                    : isLight ? 'rgba(10,22,40,0.04)' : 'rgba(255,255,255,0.04)',
                  border: `1.5px solid ${isActive
                    ? cat.color + '60'
                    : isLight ? 'rgba(10,22,40,0.10)' : 'rgba(255,255,255,0.08)'}`,
                  color: isActive
                    ? cat.color
                    : isLight ? 'rgba(10,22,40,0.50)' : 'rgba(255,255,255,0.50)',
                  transform: isActive ? 'translateY(-2px)' : 'translateY(0)',
                  boxShadow: isActive ? `0 4px 20px ${cat.color}18` : 'none',
                }}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${cat.icon} text-sm`}></i>
                </div>
                {cat.label}
                {cat.isNew && (
                  <span
                    className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-extrabold tracking-wide uppercase leading-none ml-0.5"
                    style={{
                      backgroundColor: cat.color,
                      color: '#fff',
                      lineHeight: 1,
                    }}
                  >
                    nuevo
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Active Category Panel */}
        <div
          key={activeCategory}
          className="relative rounded-3xl border p-4 sm:p-6 md:p-8 mb-8 md:mb-10"
          style={{
            borderColor: isLight ? current.color + '30' : current.color + '20',
            background: isLight
              ? `linear-gradient(145deg, ${current.color}10 0%, rgba(248,251,255,0.98) 60%)`
              : `linear-gradient(145deg, ${current.color}07 0%, rgba(6,15,35,0.95) 60%)`,
          }}
        >
          {/* Category Header */}
          <div className="flex items-center gap-3 md:gap-4 mb-5 md:mb-8">
            <div
              className="w-10 h-10 md:w-14 md:h-14 flex items-center justify-center rounded-xl md:rounded-2xl flex-shrink-0"
              style={{ backgroundColor: current.color + '18', color: current.color }}
            >
              <i className={`${current.icon} text-xl md:text-2xl`}></i>
            </div>
            <div>
              <h3
                className="font-heading text-base md:text-xl font-bold"
                style={{ color: current.color }}
              >
                {current.label}
              </h3>
              <p
                className="font-body text-xs mt-0.5"
                style={{ color: isLight ? 'rgba(10,22,40,0.40)' : 'rgba(255,255,255,0.40)' }}
              >
                {current.faqs.length} {t('faq_questions')}
              </p>
            </div>
            <div
              className="absolute top-0 right-0 w-32 h-32 rounded-3xl pointer-events-none overflow-hidden"
              aria-hidden="true"
            >
              <div
                className="absolute -top-8 -right-8 w-32 h-32 rounded-full"
                style={{ background: `radial-gradient(circle, ${current.color}18 0%, transparent 70%)` }}
              />
            </div>
          </div>

          {/* Questions Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {current.faqs.map((faq, i) => (
              <QuestionCard
                key={faq.question}
                faq={faq}
                color={current.color}
                isOpen={openQuestion === faq.question}
                onToggle={() => toggleQuestion(faq.question)}
                index={i}
                isLight={isLight}
              />
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center bg-navy-800 border border-navy-600 rounded-2xl p-8">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#25D366]/15 text-[#25D366] mx-auto mb-4">
            <i className="ri-question-answer-line text-xl"></i>
          </div>
          <h3 className="font-heading text-xl font-bold text-white mb-2">
            {t('faq_bottom_title')}
          </h3>
          <p className="font-body text-sm text-white/55 mb-6">
            {t('faq_bottom_sub')}
          </p>
          <a
            href={WHATSAPP_DR}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="inline-flex items-center gap-2 bg-[#25D366]/15 hover:bg-[#25D366]/25 text-[#25D366] border border-[#25D366]/30 font-semibold text-sm px-6 py-3 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap font-body"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-whatsapp-line text-base"></i>
            </div>
            {t('faq_bottom_cta')}
          </a>
        </div>

      </div>
    </section>
  );
}
